import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Package, ShoppingCart, LogOut, Plus, Pencil, Trash2, X, Bell, BellRing } from "lucide-react";
import Seo from "@/components/Seo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ withCredentials: true });

const EMPTY_FORM = {
    name: "", species: "", category: "snakes", morph: "", price: "", stock: 1,
    temperament: "", humidity: "", temperature: "", diet: "", care_level: "Intermediate",
    birth_year: "", lineage: "", description: "", image_url: "", rarity: "common", featured: false,
};

const inputCls =
    "w-full border border-red-950/60 bg-black/50 px-3 py-2.5 text-sm text-bone placeholder:text-ash/40 outline-none transition-colors duration-300 focus:border-red-700";

const ProductForm = ({ initial, onSave, onCancel }) => {
    const [form, setForm] = useState(initial);
    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    return (
        <form
            data-testid="admin-product-form"
            onSubmit={(e) => {
                e.preventDefault();
                onSave({ ...form, price: parseFloat(form.price), stock: parseInt(form.stock, 10) });
            }}
            className="border border-red-900/50 bg-coal p-6"
        >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <input data-testid="product-form-name" required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="product-form-species" placeholder="Species (latin)" value={form.species} onChange={set("species")} className={inputCls} />
                <select data-testid="product-form-category" value={form.category} onChange={set("category")} className={inputCls}>
                    {["snakes", "lizards", "amphibians", "invertebrates", "mammals", "plants", "fish", "merch", "caresheets"].map((c) => (
                        <option key={c}>{c}</option>
                    ))}
                </select>
                <input data-testid="product-form-morph" placeholder="Morph / variant" value={form.morph} onChange={set("morph")} className={inputCls} />
                <input data-testid="product-form-price" required type="number" min="0.01" step="0.01" placeholder="Price USD *" value={form.price} onChange={set("price")} className={inputCls} />
                <input data-testid="product-form-stock" required type="number" min="0" placeholder="Stock" value={form.stock} onChange={set("stock")} className={inputCls} />
                <select data-testid="product-form-rarity" value={form.rarity} onChange={set("rarity")} className={inputCls}>
                    <option value="common">common</option>
                    <option value="rare">rare</option>
                    <option value="ultra-rare">ultra-rare</option>
                </select>
                <select data-testid="product-form-care" value={form.care_level} onChange={set("care_level")} className={inputCls}>
                    {["Beginner", "Intermediate", "Advanced", "Expert"].map((c) => (
                        <option key={c}>{c}</option>
                    ))}
                </select>
                <input data-testid="product-form-humidity" placeholder="Humidity (e.g. 70–90%)" value={form.humidity} onChange={set("humidity")} className={inputCls} />
                <input data-testid="product-form-temperature" placeholder="Temperature" value={form.temperature} onChange={set("temperature")} className={inputCls} />
                <input data-testid="product-form-diet" placeholder="Diet" value={form.diet} onChange={set("diet")} className={inputCls} />
                <input data-testid="product-form-temperament" placeholder="Temperament" value={form.temperament} onChange={set("temperament")} className={inputCls} />
                <input data-testid="product-form-image" placeholder="Image URL" value={form.image_url} onChange={set("image_url")} className={`${inputCls} lg:col-span-2`} />
                <input data-testid="product-form-birthyear" placeholder="Birth year" value={form.birth_year} onChange={set("birth_year")} className={inputCls} />
                <input data-testid="product-form-lineage" placeholder="Lineage note" value={form.lineage} onChange={set("lineage")} className={inputCls} />
                <textarea data-testid="product-form-description" placeholder="Description" rows="2" value={form.description} onChange={set("description")} className={`${inputCls} lg:col-span-3`} />
            </div>
            <label className="mt-4 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash">
                <input data-testid="product-form-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="accent-red-700" />
                Featured specimen
            </label>
            <div className="mt-5 flex gap-3">
                <button data-testid="product-form-save" type="submit" className="bg-crimson px-6 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-red-700">
                    Save
                </button>
                <button data-testid="product-form-cancel" type="button" onClick={onCancel} className="border border-red-950/60 px-6 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash hover:text-bone">
                    Cancel
                </button>
            </div>
        </form>
    );
};

export default function AdminDashboard() {
    const [user, setUser] = useState(undefined);
    const [tab, setTab] = useState("inventory");
    const [products, setProducts] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [orders, setOrders] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [replies, setReplies] = useState({});

    const saveQuote = async (id, status, reply = "") => {
        try {
            const { data } = await http.put(`${API}/quotes/${id}`, { status, reply });
            setQuotes((prev) => prev.map((q) => (q.id === id ? data : q)));
            if (reply) {
                setReplies((prev) => ({ ...prev, [id]: "" }));
                toast.success("Quote reply emailed to the client");
            } else {
                toast.success(`Marked as ${status}`);
            }
        } catch {
            toast.error("Quote update failed");
        }
    };

    const saveOrderStatus = async (id, status) => {
        try {
            const { data } = await http.put(`${API}/orders/${id}`, { status });
            setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
            toast.success(`Order marked ${status}`);
        } catch {
            toast.error("Status update failed");
        }
    };
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        http.get(`${API}/auth/me`)
            .then((r) => {
                if (r.data.role !== "admin") navigate("/admin/login");
                else setUser(r.data);
            })
            .catch(() => navigate("/admin/login"));
    }, [navigate]);

    const loadProducts = () => http.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});

    useEffect(() => {
        if (!user) return;
        loadProducts();
        http.get(`${API}/quotes`).then((r) => setQuotes(r.data)).catch(() => {});
        http.get(`${API}/orders`).then((r) => setOrders(r.data)).catch(() => {});
        http.get(`${API}/waitlist`).then((r) => setWaitlist(r.data)).catch(() => {});
        http.get(`${API}/subscribers`).then((r) => setSubscribers(r.data)).catch(() => {});
    }, [user]);

    if (user === undefined) {
        return (
            <main className="flex min-h-screen items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-red-900 border-t-red-500" />
            </main>
        );
    }

    const saveProduct = async (data) => {
        try {
            if (editing) {
                await http.put(`${API}/products/${editing.id}`, data);
                toast.success("Specimen updated");
            } else {
                await http.post(`${API}/products`, data);
                toast.success("Specimen added to inventory");
            }
            setEditing(null);
            setAdding(false);
            loadProducts();
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
        }
    };

    const deleteProduct = async (id) => {
        try {
            await http.delete(`${API}/products/${id}`);
            toast.success("Removed from inventory");
            loadProducts();
        } catch {
            toast.error("Delete failed");
        }
    };

    const logout = async () => {
        await http.post(`${API}/auth/logout`).catch(() => {});
        navigate("/admin/login");
    };

    const tabs = [
        { key: "inventory", label: "Inventory", icon: Package, testid: "admin-tab-inventory" },
        { key: "orders", label: `Orders (${orders.length})`, icon: ShoppingCart, testid: "admin-tab-orders" },
        { key: "waitlist", label: `Waitlist (${waitlist.length})`, icon: Bell, testid: "admin-tab-waitlist" },
        { key: "subscribers", label: `Subscribers (${subscribers.length})`, icon: BellRing, testid: "admin-tab-subscribers" },
    ];

    return (
        <main className="mx-auto min-h-screen max-w-7xl px-6 pb-24 pt-32 sm:px-12" data-testid="admin-dashboard-container">
            <Seo title="Keeper Dashboard — Logan's Reptiles & Exotics" siteName="Logan's Reptiles & Exotics" />
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-red-500">The Facility</p>
                    <h1 className="mt-2 font-display text-3xl font-extrabold text-bone">Keeper Dashboard</h1>
                </div>
                <button
                    data-testid="admin-logout-button"
                    onClick={logout}
                    className="flex items-center gap-2 border border-red-950/60 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:border-red-700 hover:text-bone"
                >
                    <LogOut className="h-3.5 w-3.5" /> Logout
                </button>
            </div>

            <div className="mt-10 flex gap-2 border-b border-red-950/50">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        data-testid={t.testid}
                        onClick={() => setTab(t.key)}
                        className={`flex items-center gap-2 border-b-2 px-5 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                            tab === t.key ? "border-red-600 text-bone" : "border-transparent text-ash hover:text-bone"
                        }`}
                    >
                        <t.icon className="h-3.5 w-3.5" /> {t.label}
                    </button>
                ))}
            </div>

            {tab === "inventory" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8">
                    {!adding && !editing && (
                        <button
                            data-testid="admin-add-item-button"
                            onClick={() => setAdding(true)}
                            className="mb-6 flex items-center gap-2 bg-crimson px-5 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-red-700"
                        >
                            <Plus className="h-3.5 w-3.5" /> Add Specimen / Item
                        </button>
                    )}
                    {adding && (
                        <div className="mb-8">
                            <ProductForm initial={EMPTY_FORM} onSave={saveProduct} onCancel={() => setAdding(false)} />
                        </div>
                    )}
                    {editing && (
                        <div className="mb-8">
                            <p className="mb-3 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-red-500">
                                <Pencil className="h-3 w-3" /> Editing: {editing.name}
                            </p>
                            <ProductForm initial={editing} onSave={saveProduct} onCancel={() => setEditing(null)} />
                        </div>
                    )}
                    <div className="overflow-x-auto border border-red-950/50">
                        <table className="w-full text-left text-sm" data-testid="admin-inventory-table">
                            <thead>
                                <tr className="border-b border-red-950/50 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash">
                                    <th className="p-4">Name</th>
                                    <th className="p-4">Category</th>
                                    <th className="p-4">Price</th>
                                    <th className="p-4">Stock</th>
                                    <th className="p-4">Rarity</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p) => (
                                    <tr key={p.id} className="border-b border-red-950/30 transition-colors duration-300 hover:bg-red-950/10" data-testid="admin-inventory-row">
                                        <td className="p-4 text-bone">{p.name}</td>
                                        <td className="p-4 text-ash">{p.category}</td>
                                        <td className="p-4 font-mono2 text-bone">${p.price.toLocaleString()}</td>
                                        <td className={`p-4 font-mono2 ${p.stock > 0 ? "text-emerald-400" : "text-red-500"}`}>{p.stock}</td>
                                        <td className="p-4 text-ash">{p.rarity}</td>
                                        <td className="p-4 text-right">
                                            <button data-testid="admin-edit-item" onClick={() => { setEditing(p); setAdding(false); }} className="mr-2 p-2 text-ash transition-colors duration-300 hover:text-bone">
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button data-testid="admin-delete-item" onClick={() => deleteProduct(p.id)} className="p-2 text-ash transition-colors duration-300 hover:text-red-500">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            )}

            {tab === "quotes" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 space-y-4" data-testid="admin-quotes-list">
                    {quotes.length === 0 && (
                        <p className="py-16 text-center font-editorial text-lg italic text-ash">No quote requests yet.</p>
                    )}
                    {quotes.map((q) => (
                        <div key={q.id} className="border border-red-950/50 bg-coal p-6" data-testid="admin-quote-card">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="font-display text-base font-bold text-bone">{q.name}</p>
                                <div className="flex items-center gap-3">
                                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-red-500">
                                        ${Number(q.budget).toLocaleString()} budget
                                    </span>
                                    <span
                                        data-testid="quote-status-badge"
                                        className={`border px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] ${
                                            q.status === "built"
                                                ? "border-emerald-700/60 bg-emerald-950/40 text-emerald-400"
                                                : q.status === "accepted"
                                                  ? "border-emerald-800/50 bg-emerald-950/20 text-emerald-500"
                                                  : q.status === "quoted"
                                                    ? "border-amber-800/60 bg-amber-950/30 text-amber-400"
                                                    : "border-red-800/60 bg-red-950/30 text-red-400"
                                        }`}
                                    >
                                        {q.status || "new"}
                                    </span>
                                </div>
                            </div>
                            <p className="mt-1 font-mono2 text-xs text-ash">{q.email}</p>
                            <div className="mt-3 flex flex-wrap gap-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-ash/80">
                                <span className="border border-red-950/50 px-2 py-1">{q.dimensions}</span>
                                <span className="border border-red-950/50 px-2 py-1">{q.humidity_type}</span>
                                <span className="border border-red-950/50 px-2 py-1">{q.inhabitant}</span>
                            </div>
                            {q.message && <p className="mt-3 text-sm italic text-ash">“{q.message}”</p>}
                            {q.attachments?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2" data-testid="quote-attachments">
                                    {q.attachments.map((a, i) => (
                                        <a
                                            key={i}
                                            data-testid="quote-attachment-link"
                                            href={`${API}/files/${a.path}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="border border-red-900/50 bg-black/40 px-3 py-1.5 font-mono2 text-[10px] text-red-300 transition-colors duration-300 hover:bg-red-950/40"
                                        >
                                            {a.filename}
                                        </a>
                                    ))}
                                </div>
                            )}
                            {q.reply && (
                                <p className="mt-3 border-l-2 border-amber-700/60 pl-3 text-sm italic text-amber-200/80">
                                    Your reply: “{q.reply}”
                                </p>
                            )}
                            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="quote-status-actions">
                                {["new", "quoted", "accepted", "built"].map((s) => (
                                    <button
                                        key={s}
                                        data-testid={`quote-status-${s}`}
                                        onClick={() => saveQuote(q.id, s)}
                                        className={`border px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                                            (q.status || "new") === s
                                                ? "border-red-700 bg-crimson text-bone"
                                                : "border-red-950/60 text-ash hover:border-red-700 hover:text-bone"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <div className="mt-4 flex gap-2">
                                <input
                                    data-testid="quote-reply-input"
                                    placeholder="Write your quote reply — price, timeline, concept…"
                                    value={replies[q.id] || ""}
                                    onChange={(e) => setReplies((prev) => ({ ...prev, [q.id]: e.target.value }))}
                                    className="flex-1 border border-red-950/60 bg-black/50 px-3 py-2.5 text-xs text-bone placeholder:text-ash/40 outline-none transition-colors duration-300 focus:border-red-700"
                                />
                                <button
                                    data-testid="quote-reply-send"
                                    onClick={() => saveQuote(q.id, "quoted", replies[q.id] || "")}
                                    disabled={!(replies[q.id] || "").trim()}
                                    className="border border-amber-700/70 bg-amber-900/30 px-4 py-2.5 font-mono2 text-[9px] uppercase tracking-[0.2em] text-amber-400 transition-colors duration-300 hover:bg-amber-800/40 disabled:opacity-40"
                                >
                                    Email to client
                                </button>
                            </div>
                            <p className="mt-3 font-mono2 text-[9px] uppercase tracking-[0.2em] text-ash/40">
                                {new Date(q.created_at).toLocaleString()}
                            </p>
                        </div>
                    ))}
                </motion.div>
            )}

            {tab === "orders" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 space-y-4" data-testid="admin-orders-list">
                    {orders.length === 0 && (
                        <p className="py-16 text-center font-editorial text-lg italic text-ash">No orders yet.</p>
                    )}
                    {orders.map((o) => (
                        <div key={o.id} className="border border-red-950/50 bg-coal p-6" data-testid="admin-order-card">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="font-mono2 text-xs text-ash">{o.customer_email || "guest"}</p>
                                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-bone">
                                    ${Number(o.amount_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="order-status-actions">
                                {["paid", "shipped", "delivered", "cancelled"].map((s) => (
                                    <button
                                        key={s}
                                        data-testid={`order-status-${s}`}
                                        onClick={() => saveOrderStatus(o.id, s)}
                                        className={`border px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                                            o.status === s
                                                ? "border-red-700 bg-crimson text-bone"
                                                : "border-red-950/60 text-ash hover:border-red-700 hover:text-bone"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <ul className="mt-3 space-y-1 text-sm text-ash">
                                {o.items.map((i, idx) => (
                                    <li key={idx}>
                                        {i.quantity}× {i.name} — ${(i.unit_price * i.quantity).toLocaleString()}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-3 font-mono2 text-[9px] uppercase tracking-[0.2em] text-ash/40">
                                {new Date(o.created_at).toLocaleString()}
                            </p>
                        </div>
                    ))}
                </motion.div>
            )}

            {tab === "waitlist" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 space-y-4" data-testid="admin-waitlist-list">
                    {waitlist.length === 0 && (
                        <p className="py-16 text-center font-editorial text-lg italic text-ash">No waitlist signups yet.</p>
                    )}
                    {waitlist.map((w) => {
                        const p = products.find((x) => x.id === w.product_id);
                        return (
                            <div key={w.id} className="flex flex-wrap items-center justify-between gap-3 border border-amber-900/40 bg-coal p-6" data-testid="admin-waitlist-row">
                                <div>
                                    <p className="font-display text-base font-bold text-bone">{p ? p.name : w.product_id}</p>
                                    <p className="mt-1 font-mono2 text-xs text-amber-400">{w.email}</p>
                                    {w.notified && (
                                        <span className="mt-1 inline-block border border-emerald-800/50 bg-emerald-950/30 px-2 py-0.5 font-mono2 text-[8px] uppercase tracking-[0.2em] text-emerald-400">
                                            Restock email sent
                                        </span>
                                    )}
                                </div>
                                <div className="text-right">
                                    {p && (
                                        <p className={`font-mono2 text-[10px] uppercase tracking-[0.2em] ${p.stock > 0 ? "text-emerald-400" : "text-red-500"}`}>
                                            {p.stock > 0 ? `${p.stock} back in stock` : "sold out"}
                                        </p>
                                    )}
                                    <p className="mt-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-ash/40">
                                        {new Date(w.created_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </motion.div>
            )}

            {tab === "subscribers" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 space-y-3" data-testid="admin-subscribers-list">
                    {subscribers.length === 0 && (
                        <p className="py-16 text-center font-editorial text-lg italic text-ash">No subscribers yet.</p>
                    )}
                    {subscribers.map((s) => (
                        <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 border border-red-950/50 bg-coal px-6 py-4" data-testid="admin-subscriber-row">
                            <p className="font-mono2 text-sm text-bone">{s.email}</p>
                            <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-ash/40">
                                {new Date(s.created_at).toLocaleString()}
                            </p>
                        </div>
                    ))}
                </motion.div>
            )}

        </main>
    );
}
